/**
 * condition-editor.tsx — Các hàng điều kiện lọc kiểu SaleSmartly:
 * [Trường ▾] [Toán tử ▾] [Giá trị] [và/hoặc ▾] [🗑]. Dùng chung cho popover
 * "Lọc" ở danh sách hội thoại và hộp thoại tạo/sửa chế độ xem.
 */
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, CirclePlus, Search, Trash2 } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useTags, useOrgMembers } from '@/hooks/use-settings'
import { useInboxAccounts } from '@/hooks/use-integrations'
import { CHANNEL_GROUPS } from '@/lib/channel-groups'
import { cn } from '@/lib/utils'
import {
  changeField,
  changeOp,
  FIELD_DEFS,
  FIELD_ORDER,
  fromLocalInput,
  LOGIC_LABEL,
  ME,
  newCondition,
  NO_VALUE_OPS,
  toLocalInput,
  type ConditionField,
  type ConditionLogic,
  type InboxCondition,
  type RelativeValue,
} from './inbox-conditions'

interface Option {
  value: string
  label: string
  color?: string
}

/** Nguồn lựa chọn cho các trường cần dữ liệu từ API (nhãn, tài khoản, nhân viên). */
function useFieldOptions() {
  const { t: tr } = useTranslation('convList')
  const { data: tags = [] } = useTags()
  const { data: accounts = [] } = useInboxAccounts()
  const { data: members = [] } = useOrgMembers()
  return useMemo(() => {
    const byKind: Record<string, Option[]> = {
      tags: tags.map((t) => ({ value: t.name, label: t.name, color: t.color })),
      accounts: accounts.map((a) => ({ value: a.id, label: a.displayName ?? tr('shared.unnamed') })),
      channels: CHANNEL_GROUPS.map((g) => ({ value: g.id, label: g.label })),
      members: [
        { value: ME, label: tr('cond.meViewer') },
        ...members.map((m) => ({ value: m.id, label: m.fullName ?? tr('shared.unnamed') })),
      ],
    }
    return byKind
  }, [tags, accounts, members, tr])
}

export function ConditionRows({
  conditions,
  logic,
  onChange,
  onLogicChange,
}: {
  conditions: InboxCondition[]
  logic: ConditionLogic
  onChange: (next: InboxCondition[]) => void
  onLogicChange: (l: ConditionLogic) => void
}) {
  const { t } = useTranslation('convList')
  const options = useFieldOptions()
  const update = (i: number, c: InboxCondition) => onChange(conditions.map((x, j) => (j === i ? c : x)))
  const remove = (i: number) => onChange(conditions.filter((_, j) => j !== i))

  return (
    <div className="space-y-2">
      {conditions.length === 0 && (
        <p className="rounded-md border border-dashed px-3 py-4 text-center text-xs text-muted-foreground">
          {t('cond.noConditions')}
        </p>
      )}
      {conditions.map((c, i) => {
        const def = FIELD_DEFS[c.field]
        return (
          <div key={i} className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
            <Select value={c.field} onValueChange={(f) => update(i, changeField(c, f as ConditionField))}>
              <SelectTrigger className="w-full sm:w-48" aria-label={t('cond.fieldLabel')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FIELD_ORDER.map((f) => (
                  <SelectItem key={f} value={f}>
                    {FIELD_DEFS[f].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={c.op} onValueChange={(op) => update(i, changeOp(c, op))}>
              <SelectTrigger className="w-[calc(50%-4px)] sm:w-32" aria-label={t('cond.opLabel')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {def.ops.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="min-w-0 flex-1">
              <ValueInput condition={c} options={options} onChange={(value) => update(i, { ...c, value })} />
            </div>
            {/* Nối điều kiện: một giá trị chung cho cả bộ (như SaleSmartly) */}
            {i < conditions.length - 1 ? (
              <Select value={logic} onValueChange={(l) => onLogicChange(l as ConditionLogic)}>
                <SelectTrigger className="w-20" aria-label={t('cond.joinLabel')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="and">{LOGIC_LABEL.and}</SelectItem>
                  <SelectItem value="or">{LOGIC_LABEL.or}</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <span className="hidden w-20 sm:block" />
            )}
            <button
              type="button"
              aria-label={t('cond.removeLabel')}
              onClick={() => remove(i)}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        )
      })}
    </div>
  )
}

export function AddConditionButton({ onAdd }: { onAdd: (c: InboxCondition) => void }) {
  const { t } = useTranslation('convList')
  return (
    <button
      type="button"
      onClick={() => onAdd(newCondition())}
      className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
    >
      <CirclePlus className="h-4 w-4" />
      {t('cond.add')}
    </button>
  )
}

function ValueInput({
  condition: c,
  options,
  onChange,
}: {
  condition: InboxCondition
  options: Record<string, Option[]>
  onChange: (v: unknown) => void
}) {
  const { t } = useTranslation('convList')
  const def = FIELD_DEFS[c.field]
  if (NO_VALUE_OPS.has(c.op)) return <div className="h-8" />

  switch (def.kind) {
    case 'text':
      return (
        <Input
          value={typeof c.value === 'string' ? c.value : ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t('cond.textPlaceholder')}
          maxLength={200}
        />
      )
    case 'single':
      return (
        <Select value={typeof c.value === 'string' && c.value ? c.value : undefined} onValueChange={onChange}>
          <SelectTrigger aria-label={t('cond.valueLabel')}>
            <SelectValue placeholder={t('common:actions.choose')} />
          </SelectTrigger>
          <SelectContent>
            {def.options!.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )
    case 'time':
      return <TimeInput op={c.op} value={c.value} onChange={onChange} />
    default: {
      const list = def.kind === 'multi' ? def.options! : options[def.kind] ?? []
      return <MultiPicker options={list} value={Array.isArray(c.value) ? (c.value as string[]) : []} onChange={onChange} />
    }
  }
}

function TimeInput({ op, value, onChange }: { op: string; value: unknown; onChange: (v: unknown) => void }) {
  const { t } = useTranslation('convList')
  if (op === 'within' || op === 'not_within') {
    const r = (value as RelativeValue) ?? { amount: 1, unit: 'day' }
    return (
      <div className="flex items-center gap-2">
        <Input
          type="number"
          min={1}
          value={Number.isFinite(r.amount) ? r.amount : ''}
          onChange={(e) => onChange({ ...r, amount: Math.floor(Number(e.target.value)) })}
          className="w-20"
          aria-label={t('common:fields.quantity')}
        />
        <Select value={r.unit} onValueChange={(unit) => onChange({ ...r, unit })}>
          <SelectTrigger className="w-24" aria-label={t('cond.unitLabel')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="hour">{t('cond.unitHour')}</SelectItem>
            <SelectItem value="day">{t('cond.unitDay')}</SelectItem>
          </SelectContent>
        </Select>
        <span className="hidden text-xs text-muted-foreground sm:inline">{op === 'within' ? t('cond.recent') : t('cond.ago')}</span>
      </div>
    )
  }
  if (op === 'between') {
    const [a, b] = Array.isArray(value) ? (value as string[]) : ['', '']
    return (
      <div className="flex items-center gap-1.5">
        <Input type="datetime-local" value={toLocalInput(a)} onChange={(e) => onChange([fromLocalInput(e.target.value), b])} aria-label={t('common:time.from')} />
        <span className="text-xs text-muted-foreground">–</span>
        <Input type="datetime-local" value={toLocalInput(b)} onChange={(e) => onChange([a, fromLocalInput(e.target.value)])} aria-label={t('common:time.to')} />
      </div>
    )
  }
  return (
    <Input type="datetime-local" value={toLocalInput(value)} onChange={(e) => onChange(fromLocalInput(e.target.value))} aria-label={t('cond.momentLabel')} />
  )
}

/** Chọn nhiều: hiện chip đầu tiên + "+N" như SaleSmartly. */
function MultiPicker({ options, value, onChange }: { options: Option[]; value: string[]; onChange: (v: string[]) => void }) {
  const { t } = useTranslation('convList')
  const [query, setQuery] = useState('')
  const labelOf = (v: string) => options.find((o) => o.value === v)?.label ?? v
  const shown = query ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase())) : options
  const toggle = (v: string) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v])

  return (
    <DropdownMenu onOpenChange={(o) => !o && setQuery('')}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex h-8 w-full min-w-0 items-center gap-1 rounded-md border border-input bg-card px-2 text-left text-sm hover:border-primary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/15"
        >
          {value.length === 0 ? (
            <span className="flex-1 truncate text-muted-foreground">{t('common:actions.choose')}</span>
          ) : (
            <span className="flex min-w-0 flex-1 items-center gap-1">
              <span className="truncate rounded bg-secondary px-1.5 py-0.5 text-xs">{labelOf(value[0])}</span>
              {value.length > 1 && (
                <span className="shrink-0 rounded bg-secondary px-1.5 py-0.5 text-xs text-muted-foreground">+{value.length - 1}</span>
              )}
            </span>
          )}
          <ChevronDown className="h-4 w-4 shrink-0 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="flex max-h-[min(20rem,var(--radix-dropdown-menu-content-available-height))] w-64 flex-col">
        {options.length > 8 && (
          <div className="relative px-1 pb-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('cond.searchPlaceholder')}
              className="h-8 pl-7 text-xs"
              onKeyDown={(e) => e.stopPropagation()}
            />
          </div>
        )}
        <div className="min-h-0 overflow-y-auto">
          {shown.length === 0 && <div className="px-2 py-3 text-center text-xs text-muted-foreground">{t('cond.noOptions')}</div>}
          {shown.map((o) => (
            <DropdownMenuCheckboxItem
              key={o.value}
              checked={value.includes(o.value)}
              onCheckedChange={() => toggle(o.value)}
              onSelect={(e) => e.preventDefault()}
            >
              {o.color && <span className="mr-2 h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: o.color }} />}
              <span className={cn('truncate', o.value === ME && 'font-medium')}>{o.label}</span>
            </DropdownMenuCheckboxItem>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
